/**
 * Voice activity on raw microphone audio (16 kHz, 16-bit mono), with no model: a frame is
 * speech when it is clearly louder than the room. Used to cut "Hej Elena, pusti..." out of
 * the last seconds of audio, and to notice when you stopped talking.
 */

export const SAMPLE_RATE = 16_000;
/** 20 ms frames. */
export const FRAME = 320;
const FRAME_MS = (FRAME / SAMPLE_RATE) * 1000;

/** Loudness of each 20 ms frame, 0..1 (RMS). */
export function frameLevels(pcm: Int16Array): Float32Array {
  const frames = Math.floor(pcm.length / FRAME);
  const out = new Float32Array(frames);
  for (let f = 0; f < frames; f++) {
    let sum = 0;
    for (let i = f * FRAME; i < (f + 1) * FRAME; i++) {
      const s = (pcm[i] ?? 0) / 32768;
      sum += s * s;
    }
    out[f] = Math.sqrt(sum / FRAME);
  }
  return out;
}

/** Quietest fifth of the frames = the room; speech is well above it, and above a floor. */
export function speechThreshold(levels: Float32Array, floor = 0.01): number {
  if (!levels.length) return floor;
  const sorted = Float32Array.from(levels).sort();
  const noise = sorted[Math.floor(sorted.length * 0.2)] ?? 0;
  return Math.max(noise * 3, floor);
}

export interface Span {
  /** Sample index where the words start (padding included). */
  start: number;
  /** Sample index right after they end (padding included). */
  end: number;
}

/**
 * The last thing said in a stretch of audio: from the end, skip the silence, then walk back
 * through the words until a pause longer than `maxGapMs` (between two sentences). The wake
 * word recognizer only reports a sentence after it ended, so its words are the last ones in
 * the buffer.
 */
export function findUtterance(
  pcm: Int16Array,
  options: { maxGapMs?: number; padMs?: number; maxMs?: number; floor?: number } = {},
): Span | null {
  const maxGap = Math.round((options.maxGapMs ?? 700) / FRAME_MS);
  const pad = Math.round((options.padMs ?? 300) / FRAME_MS);
  const maxFrames = Math.round((options.maxMs ?? 15_000) / FRAME_MS);
  const levels = frameLevels(pcm);
  const th = speechThreshold(levels, options.floor);

  let last = -1;
  for (let i = levels.length - 1; i >= 0; i--) {
    if ((levels[i] ?? 0) > th) {
      last = i;
      break;
    }
  }
  if (last < 0) return null;

  let first = last;
  let gap = 0;
  for (let i = last; i >= 0 && last - i < maxFrames; i--) {
    if ((levels[i] ?? 0) > th) {
      first = i;
      gap = 0;
    } else if (++gap > maxGap) break;
  }
  return {
    start: Math.max(0, first - pad) * FRAME,
    end: Math.min(levels.length, last + 1 + pad) * FRAME,
  };
}

/**
 * Live "are you still talking": feed it 20 ms frames as they come. The noise of the line is the
 * quietest stretch of the last 5 seconds (smoothed over 200 ms, so the gaps between words count
 * but a single quiet frame does not). It may fall at once, and rise only slowly (about 2x a
 * second): a steady hum (a fan, a noisy mic on the player's side) becomes "quiet" within a few
 * seconds, while words that start right away are not taken for the room.
 */
export class SpeechTracker {
  private noise = 0.004;
  private lastSpeechAt = -1;
  private startedAt = -1;
  private speechFrames = 0;
  private readonly recent: number[] = [];
  private readonly window: number[] = [];

  constructor(private readonly floor = 0.012) {}

  /** Returns true when this frame is speech. `now` in ms. */
  push(frame: Int16Array, now: number): boolean {
    if (this.startedAt < 0) this.startedAt = now;
    const level = frameLevels(frame)[0] ?? 0;

    this.recent.push(level);
    if (this.recent.length > 10) this.recent.shift();
    const smooth = this.recent.reduce((a, b) => a + b, 0) / this.recent.length;
    this.window.push(smooth);
    if (this.window.length > 250) this.window.shift();
    const quietest = Math.min(...this.window);
    // never stuck at zero after a stretch of digital silence: it must be able to grow
    this.noise =
      quietest < this.noise ? quietest : Math.min(quietest, Math.max(this.noise, 0.001) * 1.015);

    const speech = level > Math.max(this.noise * 3, this.floor);
    if (speech) {
      this.speechFrames++;
      // a single click is not speech: two frames in a short while are
      if (this.speechFrames >= 2) this.lastSpeechAt = now;
    } else {
      this.speechFrames = Math.max(0, this.speechFrames - 1);
    }
    return speech;
  }

  /** Something was said since start. */
  get heard(): boolean {
    return this.lastSpeechAt >= 0;
  }

  /** Milliseconds since the last speech (or since start, when nothing was said yet). */
  quietFor(now: number): number {
    const from = this.lastSpeechAt >= 0 ? this.lastSpeechAt : this.startedAt;
    return from < 0 ? 0 : now - from;
  }

  /** Treat the words before this moment as heard (they came from the buffer). */
  markSpeech(now: number): void {
    this.lastSpeechAt = now;
    if (this.startedAt < 0) this.startedAt = now;
  }
}
